import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaSyncAlt } from "react-icons/fa";
import { getPaymentStatus } from "../services/paymentService";
import styles from "./PaymentStatus.module.css";

const FINAL_FAILED_STATUSES = ["failed", "expired", "canceled", "reversed"];
const SUCCESS_PAYMENT_STATUSES = [
  "paid",
  "success",
  "hold",
  "authorized",
  "blocked",
  "reserved",
];
const SUCCESS_AUTH_STATUSES = ["held", "hold", "authorized", "blocked", "reserved"];
const normalizePaymentStatus = (value) =>
  String(value || "").trim().toLowerCase();
const STATUS_CHECK_INTERVAL_MS = 4000;
const MAX_AUTOMATIC_STATUS_CHECKS = 30;

const PaymentProcessing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId =
    searchParams.get("orderId") || sessionStorage.getItem("monobankPendingOrderId");
  const paymentType = searchParams.get("type") || "";
  const isAdditionalPayment = paymentType === "additional";

  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const initialCheckStartedRef = useRef(false);
  const statusCheckInFlightRef = useRef(false);
  const checkAttemptsRef = useRef(0);
  const finishedRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const checkStatus = useCallback(
    async ({ manual = false } = {}) => {
      if (statusCheckInFlightRef.current || finishedRef.current) return;

      if (!orderId) {
        setMessage("Не вдалося знайти замовлення для перевірки оплати.");
        return;
      }

      if (manual) {
        checkAttemptsRef.current = 0;
        setMessage("");
      }

      if (checkAttemptsRef.current >= MAX_AUTOMATIC_STATUS_CHECKS) {
        setMessage(
          "Не вдалося автоматично підтвердити оплату. Натисніть «Перевірити статус» ще раз або зверніться до менеджера."
        );
        return;
      }

      checkAttemptsRef.current += 1;
      statusCheckInFlightRef.current = true;
      setIsChecking(true);

      try {
        const data = await getPaymentStatus(orderId, {
          type: isAdditionalPayment ? "additional" : undefined,
          timeout: 15000,
        });

        setStatus(data);
        const paymentStatus = normalizePaymentStatus(data.paymentStatus);
        const authStatus = normalizePaymentStatus(data.holdStatus);
        const additionalPaymentStatus = normalizePaymentStatus(
          data.additionalPaymentStatus
        );

        if (
          SUCCESS_PAYMENT_STATUSES.includes(paymentStatus) ||
          SUCCESS_AUTH_STATUSES.includes(authStatus) ||
          (isAdditionalPayment && additionalPaymentStatus === "paid")
        ) {
          finishedRef.current = true;
          setMessage("Оплата успішна");
          redirectTimerRef.current = setTimeout(() => {
            navigate(`/payment/success?orderId=${orderId}`, { replace: true });
          }, 800);
          return;
        }

        if (data.syncError) {
          setMessage(data.syncError);
          return;
        }

        if (
          FINAL_FAILED_STATUSES.includes(paymentStatus) ||
          (isAdditionalPayment &&
            ["expired", "failed"].includes(additionalPaymentStatus))
        ) {
          finishedRef.current = true;
          navigate(`/payment/failed?orderId=${orderId}`, { replace: true });
          return;
        }

        setMessage("Очікуємо підтвердження оплати від Monobank...");
      } catch (error) {
        console.error("Не вдалося перевірити статус платежу:", error);
        setMessage(
          checkAttemptsRef.current >= MAX_AUTOMATIC_STATUS_CHECKS
            ? "Не вдалося перевірити статус платежу. Натисніть «Перевірити статус» ще раз."
            : "Не вдалося перевірити статус платежу. Повторюємо спробу..."
        );
      } finally {
        statusCheckInFlightRef.current = false;
        setIsChecking(false);
      }
    },
    [isAdditionalPayment, navigate, orderId]
  );

  useEffect(() => {
    if (!initialCheckStartedRef.current) {
      initialCheckStartedRef.current = true;
      checkStatus();
    }

    const timer = setInterval(checkStatus, STATUS_CHECK_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, [checkStatus]);

  return (
    <div className={styles.statusPage}>
      <div className={styles.statusPanel}>
        {status?.orderNumber && (
          <p className={styles.meta}>Замовлення № {status.orderNumber}</p>
        )}
        <p className={styles.text}>
          {message || "Перевіряємо статус платежу..."}
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.button}
            onClick={() => checkStatus({ manual: true })}
            disabled={isChecking}
          >
            <FaSyncAlt /> {isChecking ? "Перевіряємо..." : "Перевірити статус"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessing;
